import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { toast } from 'sonner';
import { PageHeader } from '@/shared/components';
import { toApiError, useApi } from '@/shared/api';
import type { PropertyResponse, UpdatePropertyRequest } from '@/shared/api';
import { PropertyForm } from './property-form';
import type { PropertyValues } from './property-form/schema';
import { ApartmentObjectAssignment } from './property-form/ApartmentObjectAssignment';
import { propertyKeys } from './queries';

function toUpdatePayload(
  values: PropertyValues,
  property: PropertyResponse,
): UpdatePropertyRequest {
  return {
    name: values.name,
    description: values.description,
    propertyType: values.propertyType,
    timezone: values.timezone,
    address: property.address,
    occupancyAdults: values.occupancyAdults,
    occupancyChildren: values.occupancyChildren,
    bedrooms: values.bedrooms,
    bathrooms: values.bathrooms,
    currency: values.currency.toUpperCase(),
    cleaningFee: values.cleaningFee,
    baseNightlyRate: values.baseNightlyRate,
    isActive: property.isActive,
    operationalName: values.operationalName,
  };
}

export function PropertyEditPage() {
  const { id } = useParams({ from: '/_admin/properties/$id/edit' });
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: property, isLoading } = useQuery({
    queryKey: propertyKeys.detail(id),
    queryFn: () => api.properties.get(id),
  });

  const update = useMutation({
    mutationFn: async (values: UpdatePropertyRequest) => {
      await api.properties.update(id, values);
      toast.success('Apartment aktualisiert');
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
      await queryClient.invalidateQueries({ queryKey: propertyKeys.detail(id) });
      void navigate({ to: '/properties/$id', params: { id } });
    },
    onError: async (err) => {
      const apiErr = await toApiError(err);
      toast.error(apiErr.detail ?? apiErr.title);
    },
  });

  if (isLoading || !property) {
    return (
      <p className="text-ink-soft p-8 text-sm" role="status">
        Apartment wird geladen…
      </p>
    );
  }

  return (
    <>
      <PageHeader eyebrow="Bearbeiten" title={property.name} titleAccent="anpassen" />
      <ApartmentObjectAssignment property={property} />
      <PropertyForm
        initial={property}
        submitting={update.isPending}
        onSubmit={async (values) => {
          await update.mutateAsync(toUpdatePayload(values, property));
        }}
        onCancel={() => void navigate({ to: '/properties/$id', params: { id } })}
        submitLabel="Änderungen speichern"
      />
    </>
  );
}
